import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { WorkflowActivityOutputField, WorkflowTemplateActivity } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import WorkflowTemplateActivityFieldsGrid from "./WorkflowTemplateActivityFieldsGrid";
import { WorkflowTemplateDetailsContextProvider } from "./WorkflowTemplateDetailsContext";
import AddWorkflowTemplateActivityFieldDialog from "./workflow-diagram/AddWorkflowTemplateActivityFieldDialog";
import DeleteWorkflowTemplateActivityFieldDialog from "./workflow-diagram/DeleteWorkflowTemplateActivityFieldDialog";
import EditWorkflowTemplateActivityFieldDialog from "./workflow-diagram/EditWorkflowTemplateActivityFieldDialog";

const WorkflowTemplateActivityFieldsPage = () => {
  usePageTitle("Workflow Activity Fields");

  const { versionId, activityId } = useParams();
  const { clientCode, hasPermissions } = useClientContext();
  const [editedField, setEditedField] = useState<WorkflowActivityOutputField>();
  const [deletedField, setDeletedField] = useState<WorkflowActivityOutputField>();
  const [addingField, setAddingField] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const getWorkflowTemplateVersionDetails = useCallback(
    () => adminApi.getWorkflowTemplateVersionDetails(versionId ?? ""),
    [versionId]
  );

  const [templateDetails, fetchError, { setData: setTemplateDetails }] = useFetch(getWorkflowTemplateVersionDetails);

  const hasEditPermissions = useMemo(
    () => hasPermissions(["ManageWorkflows"]) && Boolean(templateDetails?.workflowTemplateVersion.isCurrent),
    [hasPermissions, templateDetails]
  );

  const workflowTemplatesPath = `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowTemplates}`;
  const templateDetailsPath = `${workflowTemplatesPath}/${versionId ?? ""}`;

  if (!versionId || !activityId) {
    return <Navigate to={workflowTemplatesPath} />;
  }

  if (fetchError) {
    logError(fetchError, "[WorkflowTemplateActivityFieldsPage] getWorkflowTemplateVersionDetails");
    return <DataLoadingFailed />;
  }

  if (templateDetails === undefined) {
    return <InlineLoader />;
  }

  const activity = templateDetails.workflowTemplateVersion.activities.find((a) => a.id === activityId);

  if (!activity) {
    return <Navigate to={templateDetailsPath} />;
  }

  const handleActivityUpdated = (updatedActivity: WorkflowTemplateActivity) => {
    setTemplateDetails((prev) => {
      if (!prev) {
        return undefined;
      }

      const diagramStep = prev.diagramStepsMap[updatedActivity.id];
      return {
        ...prev,
        workflowTemplateVersion: {
          ...prev.workflowTemplateVersion,
          activities: prev.workflowTemplateVersion.activities.map((a) =>
            a.id === updatedActivity.id ? updatedActivity : a
          ),
        },
        diagramStepsMap: diagramStep
          ? {
              ...prev.diagramStepsMap,
              [updatedActivity.id]: {
                ...diagramStep,
                activity: updatedActivity,
              },
            }
          : prev.diagramStepsMap,
      };
    });
  };

  const fields = activity.outputFieldSchema?.fields ?? [];

  const filteredFields = searchTerm
    ? fields.filter((field) => field.displayName.toLocaleLowerCase().includes(searchTerm))
    : fields;

  return (
    <WorkflowTemplateDetailsContextProvider
      hasEditPermissions={hasEditPermissions}
      workflowTemplate={templateDetails.workflowTemplateVersion}
      onActivityUpdated={handleActivityUpdated}
    >
      <GeneralPageHeader
        showDefaultBackButtonTo={templateDetailsPath}
        TitleComponent={
          <Stack spacing={0.5}>
            <Typography variant="h6">{templateDetails.workflowTemplateVersion.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="caption" color="textSecondary">
                {activity.name}
              </Typography>
              <Typography variant="caption">&bull;</Typography>
              <Typography variant="caption" color="textSecondary">
                Field Settings
              </Typography>
            </Stack>
          </Stack>
        }
      />
      <Container maxWidth="lg" disableGutters sx={{ flex: 1, display: "flex", flexDirection: "column", py: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <RecordCounter records={filteredFields.length} totalRecords={fields.length} />
          <Stack direction="row" spacing={1}>
            <SearchField debounceTimeMs={300} onSearch={(value) => setSearchTerm(value.trim().toLocaleLowerCase())} />
            {hasEditPermissions && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddingField(true)}>
                Add Field
              </Button>
            )}
          </Stack>
        </Box>

        <WorkflowTemplateActivityFieldsGrid
          fields={filteredFields}
          onEditField={setEditedField}
          onDeleteField={setDeletedField}
        />
      </Container>
      {editedField && (
        <EditWorkflowTemplateActivityFieldDialog
          activityId={activity.id}
          field={editedField}
          onClose={() => setEditedField(undefined)}
        />
      )}
      {addingField && (
        <AddWorkflowTemplateActivityFieldDialog activityId={activity.id} onClose={() => setAddingField(false)} />
      )}
      {deletedField && (
        <DeleteWorkflowTemplateActivityFieldDialog
          activityId={activity.id}
          field={deletedField}
          onClose={() => setDeletedField(undefined)}
        />
      )}
    </WorkflowTemplateDetailsContextProvider>
  );
};

export default WorkflowTemplateActivityFieldsPage;
